import {
  type AppErrorCode,
  fallbackChain,
  isLanguage,
  LANGUAGES,
  type Language,
  matchLanguage,
} from "@bt/domain";
import { en } from "@bt/locales/en";
import i18n, { type BackendModule, type ResourceKey } from "i18next";
import { initReactI18next } from "react-i18next";

/** Typecheck fails when an API error code has no string under `errors`, or a string no code. */
type ErrorKey = AppErrorCode | "generic" | "network" | "pageTitle";
type MissingErrors = Exclude<ErrorKey, keyof typeof en.errors>;
type StrayErrors = Exclude<keyof typeof en.errors, ErrorKey>;
true satisfies [MissingErrors, StrayErrors] extends [never, never] ? true : false;

/**
 * Every language's area files but English's, which is in the main bundle as the fallback and the
 * source of the keys' types. The build puts each language's files into one chunk of its own
 * (`localeChunks` in vite.shared.ts), loaded when the language is first used. The server's files
 * (mail) and the contributors' (glossary) aren't the screens'.
 */
const FILES = import.meta.glob<Record<string, ResourceKey>>(
  [
    "../../../../packages/locales/*/*.json",
    "!../../../../packages/locales/en/*.json",
    "!**/mail.json",
    "!**/glossary.json",
  ],
  { import: "default" },
);

/** A language's area files, merged as English's are (`@bt/locales/en`). */
async function load(language: Language): Promise<Record<string, ResourceKey>> {
  const files = Object.entries(FILES).filter(([path]) => path.split("/").at(-2) === language);
  return Object.assign({}, ...(await Promise.all(files.map(([, file]) => file()))));
}

/** Loads a language's strings for i18next when it is first used; a language without files has none. */
const backend: BackendModule = {
  type: "backend",
  init() {},
  read(language, _namespace, callback) {
    load(language).then(
      (strings) => callback(null, strings),
      (error: Error) => callback(error, null),
    );
  },
};

const STORAGE_KEY = "bt-language";

/** i18next's language as one of the app's languages: itself when listed, else the closest one. */
export function toLanguage(lng: string): Language {
  return isLanguage(lng) ? lng : matchLanguage([lng]);
}

function initialLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLanguage(stored)) return stored;
  } catch {
    // Storage can be unavailable; fall back to the browser language.
  }
  return matchLanguage(navigator.languages.length ? navigator.languages : [navigator.language]);
}

/**
 * Switches the screens to a language; its strings load first, so nothing shows half-switched.
 * Resolves once they show, for a message that should already be in the new language.
 */
export function setLanguage(language: Language): Promise<void> {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Not persisted; the choice still applies to this session.
  }
  return i18n.changeLanguage(language).then(() => undefined);
}

/** Resolves once the first language's strings are there; main.tsx renders after it. */
export const i18nReady = i18n
  .use(backend)
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en } },
    partialBundledLanguages: true,
    supportedLngs: [...LANGUAGES],
    lng: initialLanguage(),
    // A missing key is looked up along the language's chain: de-AT, then de, then en.
    load: "currentOnly",
    fallbackLng: (code: string) => fallbackChain(code).slice(1),
    interpolation: { escapeValue: false },
    returnNull: false,
  })
  .then(() => {
    document.documentElement.lang = i18n.language;
  });

i18n.on("languageChanged", (lng) => {
  document.documentElement.lang = lng;
});

export default i18n;
